/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import type { ViewKind, LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import Emoji from '@atlaskit/emoji/emoji';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useManageViewPermissions } from '@jira/polaris__component-permissions-store/src/useCanEditIssues.tsx';
import { useEmojiById } from '@jira/polaris__lib-emoji-picker/src/useEmoji.tsx';
import { EmojiWrapper } from '@jira/polaris__lib-emoji-wrapper/src/EmojiWrapper.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ViewItemIcon } from '../view-item-icon/index.tsx';
import { useIsReadOnly } from '../../controllers/context.tsx';
import { useIsViewKindLocked } from '../../common/utils/useIsViewKindLocked.tsx';

const emojiButtonCaption = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.title-icon-resolver.emoji-button-caption',
	defaultMessage: 'Pick an emoji',
	description: 'Button caption for emoji button if none is selected',
});

type Props = {
	viewKind: ViewKind;
	emojiId?: string | undefined;
	viewId?: LocalViewId;
	isSelected?: boolean;
};

export const ViewIcon = ({ emojiId, viewKind, isSelected }: Props): JSX.Element | null => {
	const [emoji, { loadEmoji }] = useEmojiById({ emojiId });
	const isViewKindLocked = useIsViewKindLocked();

	useEffect(() => {
		// If emojiId is provided but emoji is not loaded yet
		if (emojiId && emoji === undefined) {
			loadEmoji(emojiId);
		}
	}, [emojiId, emoji, loadEmoji]);

	// If emojiId is provided but emoji is not loaded yet show nothing
	if (emojiId && emoji === undefined) {
		return null;
	}

	// If emoji is loaded, render it
	if (emoji && !isViewKindLocked(viewKind)) {
		return (
			<EmojiWrapper>
				<Emoji emoji={emoji} fitToHeight={18} showTooltip />
			</EmojiWrapper>
		);
	}

	// Otherwise, render the default view icon
	return (
		<ViewItemIcon
			isSelected={isSelected}
			viewKind={viewKind}
			locked={isViewKindLocked(viewKind)}
			label=""
		/>
	);
};

export const TitleIconResolver = ({ viewId, isSelected, ...rest }: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const { canManageView } = useManageViewPermissions(viewId);
	const isReadOnly = useIsReadOnly();
	const isViewKindLocked = useIsViewKindLocked();

	return (
		<div
			css={[
				iconWrapperStyles,
				canManageView &&
					(fg('jpd_tree-view') ? !isReadOnly && !isViewKindLocked(rest.viewKind) : !isReadOnly) &&
					iconWrapperHoverStyles,
			]}
		>
			<div css={gridChildStyles} data-icon="polaris-icon-wrapper">
				<ViewIcon isSelected={isSelected} {...rest} />
			</div>
			<div css={gridChildStyles} data-icon="add-emoji-container">
				<EmojiAddIcon
					spacing="none"
					label={formatMessage(emojiButtonCaption)}
					color={isSelected ? token('color.icon.selected') : token('color.icon')}
				/>
			</div>
		</div>
	);
};

const iconWrapperStyles = css({
	display: 'grid',
	gridTemplateAreas: 'icon',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-icon="polaris-icon-wrapper"]': {
		display: 'block',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-icon="add-emoji-container"]': {
		display: 'none',
	},
});

const iconWrapperHoverStyles = css({
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-icon="polaris-icon-wrapper"]': {
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-icon="add-emoji-container"]': {
			display: 'block',
		},
	},
});

const gridChildStyles = css({
	gridArea: 'icon',
	alignItems: 'center',
});
